import { Asset } from 'cc';
import BundleAssetProvider from './BundleAssetProvider';
import { joinURL } from './Toolkit/Utils/Utility';
import AssetLoadingConfigType from './Config/AssetLoadingConfigType';

export default class CompositedAssetProvider<TLanguage extends string> extends BundleAssetProvider {
    protected _currentBundleAssetProvider: BundleAssetProvider = null;
    private _bundleAssetProviders: { [language in TLanguage]?: BundleAssetProvider } = {};
    private _loadingBundleAssetProvider: BundleAssetProvider = null;
    private _currentLanguage: TLanguage = null;

    public get currentLanguage() {
        return this._currentLanguage;
    }

    public get isLoadingLanguage() {
        return !!this._loadingBundleAssetProvider;
    }

    public async loadLanguage(language: TLanguage) {
        let bundleAssetProvider = this._bundleAssetProviders[language];
        if (!bundleAssetProvider) {
            const languageSpecificConfigs = this.getLanguageSpecificAssetLoadingConfig(language);
            bundleAssetProvider = new BundleAssetProvider(languageSpecificConfigs);
            bundleAssetProvider.preload();
            this._bundleAssetProviders[language] = bundleAssetProvider;
            this._loadingBundleAssetProvider = bundleAssetProvider;
        }
        await bundleAssetProvider.waitForLoaded();
        this._currentLanguage = language;
        this._loadingBundleAssetProvider = null;
        this._currentBundleAssetProvider = bundleAssetProvider;
    }

    public preload() {
        if (!this._loadingBundleAssetProvider) {
            throw new Error('No language is under loading');
        }
        return this._loadingBundleAssetProvider.preload();
    }

    public set<T extends typeof Asset>(assetName: string, assetType: T, asset: InstanceType<T>) {
        this._currentBundleAssetProvider.set(assetName, assetType, asset);
    }

    public get<T extends typeof Asset>(assetName: string, assetType: T): InstanceType<T> {
        if (!this._currentBundleAssetProvider) {
            throw new Error("Bundle hasn't loaded yet, try to use localized component.");
        }
        return this._currentBundleAssetProvider.get(assetName, assetType);
    }

    public getAll<T extends typeof Asset>(assetType: T): Record<string, InstanceType<T>> {
        return this._currentBundleAssetProvider.getAll(assetType);
    }

    public waitForLoaded(): Promise<void> {
        if (!this._loadingBundleAssetProvider) {
            throw new Error('No language is under loading');
        }
        return this._loadingBundleAssetProvider.waitForLoaded();
    }

    public getProvider(language: TLanguage): BundleAssetProvider {
        return this._bundleAssetProviders[language];
    }

    protected getLanguageSpecificAssetLoadingConfig(language: TLanguage, assetLoadingConfigs: AssetLoadingConfigType[] = this.assetLoadingConfigs) {
        return assetLoadingConfigs.map((config) => {
            const specificConfig = { ...config };
            if (specificConfig.prefix) {
                specificConfig.prefix = joinURL(specificConfig.prefix, language);
            } else {
                specificConfig.bundleName = language;
            }
            return specificConfig;
        });
    }
}
